'use client';

import { useAuth } from '@/contexts/authContext';
import { useRouter, usePathname } from 'next/navigation';
import { Menu, Transition } from '@headlessui/react';
import { Fragment, useState } from 'react';
import { TickerExtractor } from '@/components/ticker-extractor';
import { Dialog } from '@/components/ui/dialog';

interface UserAvatarV2Props {
  className?: string;
}

export function UserAvatarV2({ className = '' }: UserAvatarV2Props) {
  const { userInfo, logOut } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const [showTickerExtractorDialog, setShowTickerExtractorDialog] = useState(false);

  // Get user initial from display name or email
  const getInitial = () => {
    if (userInfo?.displayName) {
      return userInfo.displayName.charAt(0).toUpperCase();
    }
    if (userInfo?.email) {
      return userInfo.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // Generate a consistent color based on user id or email
  const getBackgroundColor = () => {
    if (!userInfo) return '#a371f7'; // Default purple
    const str = userInfo.uid || userInfo.email || '';
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = ['#a371f7', '#f7931a', '#3fb950', '#58a6ff', '#f85149'];
    return colors[Math.abs(hash) % colors.length];
  };

  // Handle navigation after tickers are added
  const handleTickersAdded = (listId: string) => {
    setShowTickerExtractorDialog(false);
    router.push('/watchlist');
  };

  const menuItems = userInfo
    ? [
      {
        label: 'Quick List',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        ),
        action: () => setShowTickerExtractorDialog(true),
        show: pathname === '/',
      },
      {
        label: 'Watchlist',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
          </svg>
        ),
        action: () => router.push('/watchlist'),
        show: true,
      },
      {
        label: 'Settings',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        ),
        action: () => router.push('/usersettings'),
        show: true,
      },
      {
        label: 'Profile',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        ),
        action: () => router.push('/profile'),
        show: true,
      },
      {
        label: 'Admin',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
          </svg>
        ),
        action: () => router.push('/admin/users'),
        show: userInfo?.isAdmin || false,
      },
      {
        label: 'Logout',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
        ),
        action: () => logOut(),
        show: true,
        divider: true,
      },
    ]
    : [
      {
        label: 'Sign In',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
          </svg>
        ),
        action: () => router.push('/signin'),
        show: true,
      },
    ];

  return (
    <>
      {/* Ticker Extractor Dialog */}
      <Dialog open={showTickerExtractorDialog} onOpenChange={setShowTickerExtractorDialog}>
        <TickerExtractor
          mode="dialog"
          userId={userInfo?.uid}
          onTickersAdded={handleTickersAdded}
          onClose={() => setShowTickerExtractorDialog(false)}
        />
      </Dialog>

      <Menu as="div" className="relative inline-block text-left">
        <Menu.Button
          className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold text-white transition-all hover:scale-105 hover:ring-2 hover:ring-[#d4a853]/50 ${className}`}
          style={{ backgroundColor: getBackgroundColor() }}
        >
          {userInfo ? getInitial() : '?'}
        </Menu.Button>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right rounded-lg bg-[#1c2128] border border-[#30363d] shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none overflow-hidden">
            <div className="py-1">
              {menuItems
                .filter((item) => item.show)
                .map((item, index) => (
                  <Fragment key={index}>
                    {item.divider && (
                      <div className="h-px bg-[#30363d] my-1" />
                    )}
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={item.action}
                          className={`${active ? 'bg-[#30363d] text-[#e6edf3]' : 'text-[#8b949e]'
                            } group flex w-full items-center px-4 py-2.5 text-sm transition-colors`}
                        >
                          <span className="mr-3">{item.icon}</span>
                          {item.label}
                        </button>
                      )}
                    </Menu.Item>
                  </Fragment>
                ))}
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </>
  );
}
